import React from 'react';
import { SEO } from './SEO';
import { Footer } from './Footer';
import { Header } from './Header';
import { Link } from 'react-router-dom';
import { Home, FileText, Shield, Mail, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

export const NotFoundPage = () => {
  // Structured data for breadcrumb navigation
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://idaki.de/"
    }]
  };

  return (
    <>
      <SEO
        title="Seite nicht gefunden (404)"
        description="Diese Seite konnte leider nicht gefunden werden. Entdecken Sie unsere Compliance-Lösungen für Spielhallen, Wettbüros und Gastronomie."
        canonical="https://idaki.de/"
        structuredData={structuredData}
      />

      {/* Prevent indexing of 404 pages */}
      <meta name="robots" content="noindex, follow" />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Header />

        {/* Main Content */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="text-center mb-12">
            {/* 404 Visual */}
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 mb-6">
                <svg
                  className="w-16 h-16 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
                404
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-4">
                Seite nicht gefunden
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Die von Ihnen gesuchte Seite existiert leider nicht. Aber keine Sorge –
                wir helfen Ihnen gerne weiter!
              </p>
            </div>
          </div>

          {/* Quick Navigation Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Homepage Link */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-400 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Home className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Zur Startseite
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Entdecken Sie, wie IDA Ihnen bei Spielerschutz, Jugendschutz und HACCP hilft.
                    </p>
                    <Link to="/">
                      <Button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
                        Zur Startseite
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Features */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-400 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Compliance-Lösungen
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Spielerschutz, Geldwäsche-Prävention und mehr – digital und DSGVO-konform.
                    </p>
                    <Link to="/#features">
                      <Button
                        variant="outline"
                        className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        Mehr erfahren
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documentation */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2 hover:border-teal-400 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Rechtliche Informationen
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Datenschutz, AGB und weitere wichtige Dokumente finden Sie hier.
                    </p>
                    <div className="flex flex-col space-y-2">
                      <Link
                        to="/datenschutz"
                        className="text-teal-600 hover:text-teal-800 hover:underline text-sm font-medium"
                      >
                        → Datenschutzerklärung
                      </Link>
                      <Link
                        to="/impressum"
                        className="text-teal-600 hover:text-teal-800 hover:underline text-sm font-medium"
                      >
                        → Impressum
                      </Link>
                      <Link
                        to="/agb"
                        className="text-teal-600 hover:text-teal-800 hover:underline text-sm font-medium"
                      >
                        → AGB
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="hover:shadow-xl transition-all duration-300 border-2 hover:border-orange-400 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Kontakt aufnehmen
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Haben Sie Fragen? Unser Team hilft Ihnen gerne weiter.
                    </p>
                    <div className="space-y-2">
                      <a
                        href="mailto:info@idaki.de"
                        className="block text-orange-600 hover:text-orange-800 hover:underline text-sm font-medium"
                      >
                        → info@idaki.de
                      </a>
                      <a
                        href="tel:+4917623943896"
                        className="block text-orange-600 hover:text-orange-800 hover:underline text-sm font-medium"
                      >
                        → +49 (0) 176 23 94 38 96
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Additional Help Text */}
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Wenn Sie glauben, dass hier eine Seite sein sollte, kontaktieren Sie uns bitte.
            </p>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default NotFoundPage;
